import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {FuseNavigationService} from '@fuse/components/navigation/navigation.service';
import {FuseNavigation} from '@fuse/types';
import {adminFeature} from 'app/constants/role-settings/admin';
import {customerFeature} from 'app/constants/role-settings/customer';
import {defaultFeatureVisible} from 'app/constants/role-settings/defaults';
import {userFeature} from 'app/constants/role-settings/user';
import {adminNavigation} from 'app/navigation/admin-navigation';
import {customerNavigation} from 'app/navigation/customer-navigation';
import {navigation} from 'app/navigation/navigation';
import {ROLE_SETTINGS, SERVER_API_URL} from 'app/app.constants';
import {IRole, Role} from 'app/blocks/model/role.model';
import {Resource} from 'app/constants/resource';
import {upperCase} from 'lodash';
import EntityCrudService from 'app/blocks/service/api/entity-service';
import {BehaviorSubject, map, Observable} from 'rxjs';
import {Principal} from 'app/core/auth/principal.service';
import {screenReads} from 'app/blocks/service/assistant-context/assistant-diagnostics';

@Injectable({
    providedIn: 'root'
})
export class RoleService extends EntityCrudService<IRole> {
    roleSettings = new BehaviorSubject<any>({});
    constructor(
        http: HttpClient,
        private _fuseNavigationService: FuseNavigationService,
        private principal: Principal
    ) {
        super(http, Resource.ROLES, (obj?) => new Role(obj));
    }

    getRole = (id: number): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + `api/roles/${id}`).toPromise();
    };

    getRoleByName = (roleName: string): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + `api/roles/name/${roleName}`).toPromise();
    };

    getRoleDefaults = (authorityName: string): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + `api/roles/role-defaults/authority/${authorityName}`).toPromise();
    };

    getRoleByAuthority = (authorityName: string): Promise<any[]> => {
        return this.http.get<any>(SERVER_API_URL + `api/roles/authority/${authorityName}`).toPromise();
    };

    deleteRole = (id: number): Promise<any> => {
        return this.http.delete<any>(SERVER_API_URL + `api/roles/${id}`).toPromise();
    };

    saveRole = (role: any): Promise<any> => {
        return this.http.post<any>(SERVER_API_URL + 'api/roles', role).toPromise();
    };

    getTenantMenu = (): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + 'api/roles/settings').toPromise();
    };

    getMenuByAuthority = (authority: string, filters: any): FuseNavigation[] => {
        switch (authority) {
            case 'ROLE_ADMIN':
                return this.addVisibility(adminNavigation.concat(adminFeature), filters);
            case 'ROLE_CUSTOMER':
                return this.addVisibility(customerNavigation.concat(customerFeature), filters);
            default:
                return this.addVisibility(navigation.concat(userFeature), filters);
        }
    };

    addVisibility = (nav: FuseNavigation[], filters: any) => {
        const menuList = [];
        const tenantId = this.principal.getTenantId();
        for (const item of nav) {
            if (item.tenants && item.tenants.length > 0) {
                if (item.tenants.includes(tenantId)) {
                    const menu = {
                        id: item.id,
                        title: item.title,
                        visible: filters != null && filters[item.id] != null ? filters[item.id] : defaultFeatureVisible(item.id),
                        type: upperCase(item.type),
                        children: item.children ? this.addVisibility(item.children, filters) : null,
                        translationKey: item.translate
                    };
                    menuList.push(menu);
                }
            } else {
                const menu = {
                    id: item.id,
                    title: item.title,
                    visible: filters != null && filters[item.id] != null ? filters[item.id] : true,
                    type: upperCase(item.type),
                    children: item.children ? this.addVisibility(item.children, filters) : null,
                    translationKey: item.translate
                };
                menuList.push(menu);
            }
        }
        return menuList;
    };

    updateNavigation = (): void => {
        this.getTenantMenu().then((filters) => {
            if (filters) {
                Object.getOwnPropertyNames(filters).forEach((key: any) => {
                    this._fuseNavigationService.updateNavigationItem(key, {
                        hidden: !filters[key]
                    });
                });
            }
            this._fuseNavigationService.getCurrentNavigation().forEach((navOptions) => {
                this.disableNavForTenant(navOptions);
            });
        });
    };

    disableNavForTenant = (nav: FuseNavigation) => {
        const tenantId = this.principal.getTenantId();
        if (nav.tenants && nav.tenants.length > 0) {
            if (!nav.tenants.includes(tenantId)) {
                this._fuseNavigationService.updateNavigationItem(nav.id, {
                    hidden: true
                });
            }
        } else {
            if (nav.children && nav.children.length > 0) {
                nav.children.forEach((child) => {
                    this.disableNavForTenant(child);
                });
            }
        }
    };

    getTenantProfile = (): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + 'api/roles/profiles').toPromise();
    };

    getApiAccessKeys = (): Promise<any[]> => {
        return this.http.get<any[]>(SERVER_API_URL + 'api/roles/api-access-keys').toPromise();
    };

    getSupportedAIEntities = (): Promise<any[]> => {
        return this.http.get<any[]>(SERVER_API_URL + 'api/ai/supported-entities').toPromise();
    };

    subscribeToShowSetting = (key): Observable<any> => {
        return this.roleSettings.pipe(
            map((settings) => {
                const show = !settings || settings[key + '-show'] == null ? true : settings[key + '-show'];
                screenReads.check(key + '-show', show); // CoPilot attached context: what this screen checked
                return show;
            })
        );
    };

    subscribeToEnableSetting = (key): Observable<any> => {
        return this.roleSettings.pipe(
            map((settings) => {
                const enabled = !settings || settings[key + '-enable'] == null ? true : settings[key + '-enable'];
                screenReads.check(key + '-enable', enabled); // CoPilot attached context: what this screen checked
                return enabled;
            })
        );
    };

    showSetting = (key): boolean => {
        const settings = this.roleSettings.value;
        const show = !settings || settings[key + '-show'] == null ? true : settings[key + '-show'];
        screenReads.check(key + '-show', show); // CoPilot attached context: what this screen checked
        return show;
    };

    enableSetting = (key): boolean => {
        const settings = this.roleSettings.value;
        const enabled = !settings || settings[key + '-enable'] == null ? true : settings[key + '-enable'];
        screenReads.check(key + '-enable', enabled); // CoPilot attached context: what this screen checked
        return enabled;
    };
}
